import styled, { css, DefaultTheme } from "styled-components";

import { getColors } from "@sso/theme/colors";
import { ColorType, FontWeightType } from "@sso/types";

interface ButtonProps {
  borderRadius?: string;
  fullWidth?: boolean;
  fullHeight?: boolean;
  fontSize?: string;
  padding?: string;
  margin?: string;
  variant?: "contained" | "outlined" | "text";
  color?: ColorType;
  fontWeight?: FontWeightType;
}

const getVariantStyles = ({ variant, color, theme }: ButtonProps & { theme: DefaultTheme }) => {
  const { _color, _contrastColor, _hoverColor } = getColors(theme, color);
  switch (variant) {
    case "text":
      return css`
        background: transparent;
        color: ${color ? _color : theme.colors.text.primary};
        &:hover {
          color: ${color ? _color : theme.colors.primary.dark};
          background: #0000000d;
        }
        &:active,
        &:disabled {
          color: ${color ? _color : theme.colors.text.primary};
          background: transparent;
        }
      `;
    case "outlined":
      return css`
        background: transparent;
        border: 1px solid ${color ? _color : theme.colors.primary.main};
        color: ${color ? _color : theme.colors.text.primary};
        &:hover {
          background: ${color ? _color : theme.colors.primary.main};
          color: ${color ? _contrastColor : theme.colors.primary.contrastText};
        }
        &:active,
        &:disabled {
          background: transparent;
          color: ${color ? _color : theme.colors.text.primary};
        }
      `;
    default:
      return css`
        background: ${color ? _color : theme.colors.primary.main};
        color: ${color ? _contrastColor : theme.colors.primary.contrastText};
        border: 1px solid ${color ? _color : theme.colors.primary.main};
        &:hover {
          background: ${color ? _hoverColor : theme.colors.primary.dark};
        }
        &:active,
        &:disabled {
          background: ${color ? _color : theme.colors.primary.main};
        }
      `;
  }
};

export const Button = styled.button.attrs(p => ({
  type: p.type ? p.type : "button",
}))<ButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  user-select: none;
  cursor: pointer;
  transition: all 0.3s;
  margin: ${p => p.margin || "0.2rem"};
  padding: ${p => p.padding || "0.5rem 1rem"};
  font-size: ${p => p.fontSize || "1.1rem"};
  border-radius: ${p => p.borderRadius || p.theme.borderRadius};
  ${p => p.fontWeight && `font-weight:${p.fontWeight}`};
  ${p => p.fullHeight && "height: 100%"};
  ${p => p.fullWidth && "width: 100%"};
  &:disabled {
    opacity: ${p => p.theme.colors.action.disabledOpacity};
    cursor: default;
  }

  /* default value is contained and gradient*/
  ${getVariantStyles}
`;
